import { useState } from 'react';

import { Alert } from '@zooplus/ui-components';

import styles from '../BaseModal/BaseModal.module.scss';

import BaseModal from '@components/BaseModal/BaseModal';
import CreditCard from '@components/CreditCard/CreditCard';
import { useTranslation } from '@hooks/useTranslations';
import { useCreditcardService } from '@providers/CreditCardsMutationProvider';
import { OverlayIds, useOverlays } from '@providers/OverlaysProvider';
import { captureException } from '@services/sentryService';

/**
 * Modal to make the customer confirms that he really wants to delete the given stored credit card.
 * Independent from the actual payment service provider.
 */
const DeleteCardModal = () => {
  const { t } = useTranslation();
  const { deleteCard, data } = useCreditcardService();
  const [deleteCardError, setDeleteCardError] = useState<string>();
  const [deleteCardLoading, setDeleteCardLoading] = useState(false);
  const { hideOverlay } = useOverlays();

  if (!data.cardToDelete) {
    return null;
  }

  const handleDeleteCard = () => {
    setDeleteCardLoading(true);
    setDeleteCardError(undefined);

    deleteCard()
      .then(() => {
        hideOverlay(OverlayIds.DELETE_CARD);
        setDeleteCardLoading(false);
      })
      .catch((error: unknown) => {
        // on any unexpected error, send it to Sentry and display a generic error message to the user
        captureException({ error });
        setDeleteCardError(t('checkout.payment.creditcard.error.deletecard.apicall'));
        setDeleteCardLoading(false);
      });
  };

  return (
    <BaseModal
      modalId={OverlayIds.DELETE_CARD}
      disabledPrimaryBtn={deleteCardLoading}
      disabledSecondaryBtn={deleteCardLoading}
      isLoading={deleteCardLoading}
      handleSubmit={handleDeleteCard}
      heading={t('checkout.payment.creditcard.delete')}
      primaryButtonLabel={t('general.button.delete')}
      headingId={`delete-card-${data.cardToDelete?.pspIdentifier}`}
      closeBtnAriaLabel={t('checkout.payment.creditcard.delete')}
      dataZta="modal-removeCard"
      size="sm">
      {deleteCardError && (
        <Alert className={styles.errorMessage} variant="warning">
          {deleteCardError}
        </Alert>
      )}
      <div className={styles.confirmDeleteMessage}>
        {t('checkout.payment.creditcard.delete.confirm')}
      </div>
      <div className={styles.confirmDeleteCardContainer}>
        {data.cardToDelete && <CreditCard creditCard={data.cardToDelete} showIcons={false} />}
      </div>
    </BaseModal>
  );
};

export default DeleteCardModal;
